/**
 * Compliance Evidence Dashboard (DRR-176)
 *
 * Per-provision compliance status with evidence drill-down.
 * Answers: "Show me proof of 144G.05 Subd 3" with actual evidence artifacts.
 *
 * Two views:
 * 1. Overview — all rulesets with coverage % (regulatoryCoverageOverview)
 * 2. Detail — click a ruleset to see per-provision status (complianceStatus)
 *
 * Backend-driven: all data from GraphQL, no hardcoded provisions.
 */

import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';

import {
  useGetCoverageOverviewQuery,
  useGetComplianceStatusQuery,
  useAttestProvisionMutation,
} from '@/generated/graphql';
import {
  IconShieldCheck,
  IconShieldX,
  IconAlertTriangle,
  IconEye,
  IconCamera,
  IconFileText,
  IconCertificate,
  IconUsers,
  IconChecklist,
  IconArrowLeft,
  IconUpload,
  IconCheck,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
  Loader,
  Table,
  Button,
  Progress,
  Modal,
  List,
  Divider,
  RingProgress,
  Tooltip,
  Select,
  Textarea,
} from '@mantine/core';
// ============================================================================
// GraphQL Queries
// ============================================================================

// ============================================================================
// Types
// ============================================================================

interface AttestResult {
  attestProvision: {
    success: boolean;
    attestationId: string;
    message: string;
  };
}

interface CoverageSummary {
  rulesetId: string;
  displayName: string;
  description: string;
  jurisdiction: string;
  setting: string;
  safetyLevel: string;
  totalRules: number;
  coveredRules: number;
  gapCount: number;
  coveragePercent: number;
}

interface EvidenceRequirement {
  evidenceType: string;
  description: string;
  renewal: string;
}

interface ProvisionStatus {
  ruleId: string;
  name: string;
  sectionRef: string;
  description: string;
  status: string;
  linkedTaskGroups: string[];
  requiredEvidence: EvidenceRequirement[];
  taskEvidenceCount: number;
  manualEvidenceCount: number;
}

interface ComplianceStatus {
  rulesetId: string;
  rulesetDisplayName: string;
  totalProvisions: number;
  evidencedCount: number;
  gapCount: number;
  pendingCount: number;
  compliancePercent: number;
  provisions: ProvisionStatus[];
}

// ============================================================================
// Constants
// ============================================================================

const STATUS_CONFIG: Record<string, { color: string; label: string; icon: typeof IconShieldCheck }> = {
  EVIDENCED: { color: 'green', label: 'Evidenced', icon: IconShieldCheck },
  PENDING: { color: 'blue', label: 'Config Ready', icon: IconChecklist },
  GAP: { color: 'red', label: 'Gap', icon: IconShieldX },
  EXPIRED: { color: 'orange', label: 'Expired', icon: IconAlertTriangle },
};

const EVIDENCE_TYPE_ICONS: Record<string, typeof IconCamera> = {
  observation: IconCamera,
  document: IconFileText,
  certification: IconCertificate,
  task: IconChecklist,
  witness: IconUsers,
};

const SAFETY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'green',
};

function getCoverageColor(pct: number): string {
  if (pct >= 80) return 'green';
  if (pct >= 50) return 'yellow';
  return 'red';
}

// ============================================================================
// Provision Detail Modal
// ============================================================================

function ProvisionDetailModal({
  provision,
  rulesetId,
  opened,
  onClose,
  onAttested,
}: {
  provision: ProvisionStatus | null;
  rulesetId: string;
  opened: boolean;
  onClose: () => void;
  onAttested: () => void;
}) {
  const [attestType, setAttestType] = useState<string | null>(null);
  const [attestDesc, setAttestDesc] = useState('');
  const [attestSuccess, setAttestSuccess] = useState<string | null>(null);
  const [attest, { loading: attesting }] = useAttestProvisionMutation();

  const handleClose = () => {
    setAttestType(null);
    setAttestDesc('');
    setAttestSuccess(null);
    onClose();
  };

  const handleAttest = async () => {
    if (!provision || !attestType) return;
    const result = await attest({
      variables: {
        input: {
          rulesetId,
          provisionRef: provision.ruleId,
          evidenceType: attestType,
          description: attestDesc,
          documentIds: [],
        },
      },
    });
    if (result.data?.attestProvision.success) {
      setAttestSuccess(result.data.attestProvision.message);
      setAttestType(null);
      setAttestDesc('');
      onAttested();
    }
  };

  if (!provision) return null;

  const statusConfig = STATUS_CONFIG[provision.status] ?? STATUS_CONFIG.GAP;

  // Build evidence type options from required evidence or defaults
  const evidenceOptions = provision.requiredEvidence.length > 0
    ? provision.requiredEvidence.map((r) => ({
        value: r.evidenceType,
        label: `${r.evidenceType.charAt(0).toUpperCase() + r.evidenceType.slice(1)} — ${r.description}`,
      }))
    : [
        { value: 'observation', label: 'Observation — Photo or visual verification' },
        { value: 'document', label: 'Document — Policy, procedure, or report' },
        { value: 'certification', label: 'Certification — License, certificate, or credential' },
        { value: 'witness', label: 'Witness — Supervisor attestation' },
      ];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <Badge color={statusConfig?.color ?? 'gray'} variant="filled" size="sm">
            {statusConfig?.label ?? 'Unknown'}
          </Badge>
          <Text fw={600} size="sm">{provision.sectionRef}</Text>
        </Group>
      }
      size="lg"
    >
      <Stack gap="md">
        <div>
          <Text size="sm" fw={600}>{provision.name.replace(/_/g, ' ')}</Text>
          <Text size="sm" c="dimmed" mt={4}>{provision.description}</Text>
        </div>

        {/* Evidence Requirements */}
        {provision.requiredEvidence.length > 0 && (
          <>
            <Divider label="Evidence Required" labelPosition="left" />
            <Stack gap="xs">
              {provision.requiredEvidence.map((req, i) => {
                const EvidIcon = EVIDENCE_TYPE_ICONS[req.evidenceType] ?? IconFileText;
                return (
                  <Card key={i} withBorder p="xs">
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light">
                        <EvidIcon size={14} />
                      </ThemeIcon>
                      <div style={{ flex: 1 }}>
                        <Text size="xs" fw={500}>{req.description}</Text>
                        <Group gap="xs" mt={2}>
                          <Badge size="xs" variant="outline">{req.evidenceType}</Badge>
                          <Badge size="xs" variant="outline" color="gray">renewal: {req.renewal}</Badge>
                        </Group>
                      </div>
                    </Group>
                  </Card>
                );
              })}
            </Stack>
          </>
        )}

        {/* Linked Task Groups */}
        {provision.linkedTaskGroups.length > 0 && (
          <>
            <Divider label="Linked Configs" labelPosition="left" />
            <List size="xs" spacing={4}>
              {provision.linkedTaskGroups.map((tg) => (
                <List.Item key={tg}>
                  <Badge size="xs" variant="light">{tg}</Badge>
                </List.Item>
              ))}
            </List>
          </>
        )}

        {/* Evidence Counts */}
        <Divider label="Evidence on File" labelPosition="left" />
        <SimpleGrid cols={2}>
          <Card withBorder p="xs">
            <Text size="xs" c="dimmed">Task Completions</Text>
            <Text size="lg" fw={700}>{provision.taskEvidenceCount}</Text>
          </Card>
          <Card withBorder p="xs">
            <Text size="xs" c="dimmed">Manual Attestations</Text>
            <Text size="lg" fw={700}>{provision.manualEvidenceCount}</Text>
          </Card>
        </SimpleGrid>

        {/* Submit Evidence */}
        {attestSuccess ? (
          <Alert color="green" icon={<IconCheck size={16} />} variant="light">
            {attestSuccess}
          </Alert>
        ) : (
          <>
            <Divider label="Submit Evidence" labelPosition="left" />
            <Select
              label="Evidence Type"
              placeholder="Select evidence type..."
              data={evidenceOptions}
              value={attestType}
              onChange={setAttestType}
            />
            <Textarea
              label="Description"
              placeholder="Describe the evidence (e.g., 'License posted on wall near main entrance')"
              value={attestDesc}
              onChange={(e) => setAttestDesc(e.currentTarget.value)}
              minRows={2}
            />
            <Button
              leftSection={<IconUpload size={14} />}
              onClick={handleAttest}
              loading={attesting}
              disabled={!attestType}
            >
              Submit Evidence
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}

// ============================================================================
// Overview View — All Rulesets
// ============================================================================

function OverviewView({
  summaries,
  loading,
  onSelect,
}: {
  summaries: CoverageSummary[];
  loading: boolean;
  onSelect: (rulesetId: string) => void;
}) {
  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading regulatory coverage...</Text>
      </Stack>
    );
  }

  if (summaries.length === 0) {
    return (
      <Alert color="blue" variant="light">
        No rulesets found. Seed regulatory data first.
      </Alert>
    );
  }

  // Aggregate stats
  const totalProvisions = summaries.reduce((a, s) => a + s.totalRules, 0);
  const totalCovered = summaries.reduce((a, s) => a + s.coveredRules, 0);
  const totalGaps = summaries.reduce((a, s) => a + s.gapCount, 0);
  const overallPct = totalProvisions > 0 ? Math.round((totalCovered / totalProvisions) * 100) : 0;

  return (
    <Stack gap="lg">
      {/* Aggregate stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card withBorder p="sm">
          <Group gap="xs">
            <RingProgress size={50} thickness={5} sections={[{ value: overallPct, color: getCoverageColor(overallPct) }]} label={<Text size="xs" ta="center" fw={700}>{overallPct}%</Text>} />
            <div>
              <Text size="xs" c="dimmed">Overall Coverage</Text>
              <Text size="sm" fw={600}>{totalCovered}/{totalProvisions}</Text>
            </div>
          </Group>
        </Card>
        <Card withBorder p="sm">
          <Text size="xs" c="dimmed">Rulesets</Text>
          <Text size="lg" fw={700}>{summaries.length}</Text>
        </Card>
        <Card withBorder p="sm">
          <Text size="xs" c="dimmed">Total Provisions</Text>
          <Text size="lg" fw={700}>{totalProvisions}</Text>
        </Card>
        <Card withBorder p="sm">
          <Group gap="xs">
            <ThemeIcon size="sm" color="red" variant="light"><IconShieldX size={14} /></ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Gaps</Text>
              <Text size="lg" fw={700} c="red">{totalGaps}</Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Ruleset cards */}
      <Stack gap="sm">
        {summaries.map((s) => (
          <Card
            key={s.rulesetId}
            withBorder
            p="sm"
            style={{ cursor: 'pointer' }}
            onClick={() => onSelect(s.rulesetId)}
          >
            <Group justify="space-between">
              <div style={{ flex: 1 }}>
                <Group gap="xs" mb={4}>
                  <Text size="sm" fw={600}>{s.displayName}</Text>
                  <Badge size="xs" variant="light" color={SAFETY_COLORS[s.safetyLevel] ?? 'gray'}>
                    {s.safetyLevel}
                  </Badge>
                  {s.jurisdiction && (
                    <Badge size="xs" variant="outline">{s.jurisdiction}</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed" lineClamp={1}>{s.description}</Text>
              </div>
              <Group gap="md">
                <div style={{ width: 120 }}>
                  <Group justify="space-between" mb={4}>
                    <Text size="xs" c="dimmed">{s.coveredRules}/{s.totalRules}</Text>
                    <Text size="xs" fw={600} c={getCoverageColor(s.coveragePercent)}>
                      {s.coveragePercent}%
                    </Text>
                  </Group>
                  <Progress
                    value={s.coveragePercent}
                    color={getCoverageColor(s.coveragePercent)}
                    size="sm"
                  />
                </div>
                {s.gapCount > 0 && (
                  <Badge color="red" variant="light" size="sm">{s.gapCount} gaps</Badge>
                )}
                <IconEye size={16} color="var(--mantine-color-dimmed)" />
              </Group>
            </Group>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

// ============================================================================
// Detail View — Per-Provision for a Ruleset
// ============================================================================

function DetailView({
  rulesetId,
  onBack,
}: {
  rulesetId: string;
  onBack: () => void;
}) {
  const { data, loading, error, refetch } = useGetComplianceStatusQuery({ variables: { rulesetId } },
  );

  const [selectedProvision, setSelectedProvision] = useState<ProvisionStatus | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const status = data?.complianceStatus;

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Analyzing provisions...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        {error.message}
      </Alert>
    );
  }

  if (!status) return null;

  const handleProvisionClick = (p: ProvisionStatus) => {
    setSelectedProvision(p);
    openModal();
  };

  return (
    <Stack gap="lg">
      <Group>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={onBack}
        >
          All Rulesets
        </Button>
      </Group>

      <div>
        <Title order={3}>{status.rulesetDisplayName}</Title>
        <Text size="sm" c="dimmed">{status.totalProvisions} provisions</Text>
      </div>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card withBorder p="sm">
          <Group gap="xs">
            <RingProgress
              size={50} thickness={5}
              sections={[{ value: status.compliancePercent, color: getCoverageColor(status.compliancePercent) }]}
              label={<Text size="xs" ta="center" fw={700}>{status.compliancePercent}%</Text>}
            />
            <div>
              <Text size="xs" c="dimmed">Coverage</Text>
            </div>
          </Group>
        </Card>
        <Card withBorder p="sm">
          <Group gap="xs">
            <ThemeIcon size="sm" color="green" variant="light"><IconShieldCheck size={14} /></ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Evidenced</Text>
              <Text size="lg" fw={700} c="green">{status.evidencedCount}</Text>
            </div>
          </Group>
        </Card>
        <Card withBorder p="sm">
          <Group gap="xs">
            <ThemeIcon size="sm" color="blue" variant="light"><IconChecklist size={14} /></ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Config Ready</Text>
              <Text size="lg" fw={700} c="blue">{status.pendingCount}</Text>
            </div>
          </Group>
        </Card>
        <Card withBorder p="sm">
          <Group gap="xs">
            <ThemeIcon size="sm" color="red" variant="light"><IconShieldX size={14} /></ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Gaps</Text>
              <Text size="lg" fw={700} c="red">{status.gapCount}</Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Provision table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Status</Table.Th>
            <Table.Th>Section</Table.Th>
            <Table.Th>Provision</Table.Th>
            <Table.Th>Evidence Required</Table.Th>
            <Table.Th>Evidence On File</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {status.provisions.map((p) => {
            const cfg = STATUS_CONFIG[p.status] ?? STATUS_CONFIG.GAP;
            const totalEvidence = p.taskEvidenceCount + p.manualEvidenceCount;
            return (
              <Table.Tr
                key={p.ruleId}
                style={{ cursor: 'pointer' }}
                onClick={() => handleProvisionClick(p)}
              >
                <Table.Td>
                  <Badge color={cfg?.color ?? 'gray'} variant="light" size="sm">{cfg?.label ?? 'Unknown'}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" fw={600}>{p.sectionRef}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" lineClamp={1}>{p.description}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {p.requiredEvidence.map((req, i) => {
                      const EIcon = EVIDENCE_TYPE_ICONS[req.evidenceType] ?? IconFileText;
                      return (
                        <Tooltip key={i} label={`${req.evidenceType}: ${req.description}`} withArrow>
                          <ThemeIcon size="xs" variant="light">
                            <EIcon size={10} />
                          </ThemeIcon>
                        </Tooltip>
                      );
                    })}
                    {p.requiredEvidence.length === 0 && (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {totalEvidence > 0 ? (
                    <Badge color="green" variant="light" size="xs">{totalEvidence}</Badge>
                  ) : (
                    <Text size="xs" c="dimmed">none</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <ProvisionDetailModal
        provision={selectedProvision}
        rulesetId={rulesetId}
        opened={modalOpened}
        onClose={closeModal}
        onAttested={() => refetch()}
      />
    </Stack>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function ComplianceEvidencePage() {
  const [selectedRuleset, setSelectedRuleset] = useState<string | null>(null);

  const { data, loading, error } = useGetCoverageOverviewQuery();

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Compliance Evidence</Title>
          <Text size="sm" c="dimmed">
            Per-provision compliance status with evidence drill-down
          </Text>
        </div>
        <Badge variant="light" color="blue" size="lg">DRR-176</Badge>
      </Group>

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error" mb="lg">
          {error.message}
        </Alert>
      )}

      {selectedRuleset ? (
        <DetailView
          rulesetId={selectedRuleset}
          onBack={() => setSelectedRuleset(null)}
        />
      ) : (
        <OverviewView
          summaries={data?.regulatoryCoverageOverview ?? []}
          loading={loading}
          onSelect={setSelectedRuleset}
        />
      )}
    </Container>
  );
}
